import { api } from "./client";
import type {
  MealEntry,
  MealEntryIn,
  NutritionDailySummaryView,
  NutritionProfile,
  NutritionTargets,
  WeightTrend,
} from "@/types";

// Profile
export async function getNutritionProfile(): Promise<NutritionProfile> {
  const { data } = await api.get<NutritionProfile>("/nutrition/profile");
  return data;
}

export async function updateNutritionProfile(
  payload: Partial<NutritionProfile>,
): Promise<NutritionProfile> {
  const { data } = await api.put<NutritionProfile>("/nutrition/profile", payload);
  return data;
}

// Weight
export async function logWeight(payload: {
  date?: string;
  weight_kg: number;
  body_fat_pct?: number;
  notes?: string;
  source?: string;
}): Promise<{ id: number; date: string; weight_kg: number }> {
  const { data } = await api.post("/nutrition/weight", payload);
  return data;
}

export async function getWeightTrend(days = 90): Promise<WeightTrend> {
  const { data } = await api.get<WeightTrend>(`/nutrition/weight/trend?days=${days}`);
  return data;
}

// Meals
export async function createMeal(payload: MealEntryIn): Promise<MealEntry> {
  const { data } = await api.post<MealEntry>("/nutrition/meals", payload);
  return data;
}

export async function listTodayMeals(): Promise<MealEntry[]> {
  const { data } = await api.get<MealEntry[]>("/nutrition/meals/today");
  return data;
}

export async function listDayMeals(day: string): Promise<MealEntry[]> {
  const { data } = await api.get<MealEntry[]>(`/nutrition/meals/day/${day}`);
  return data;
}

export async function updateMeal(
  id: number,
  payload: Partial<MealEntryIn>,
): Promise<MealEntry> {
  const { data } = await api.put<MealEntry>(`/nutrition/meals/${id}`, payload);
  return data;
}

export async function deleteMeal(id: number): Promise<void> {
  await api.delete(`/nutrition/meals/${id}`);
}

// Targets + summary
export async function getTargetsToday(): Promise<NutritionTargets> {
  const { data } = await api.get<NutritionTargets>("/nutrition/targets/today");
  return data;
}

export async function getSummaryToday(): Promise<NutritionDailySummaryView> {
  const { data } = await api.get<NutritionDailySummaryView>("/nutrition/summary/today");
  return data;
}


// --- Meal items --------------------------------------------------------

export async function getMeal(id: number): Promise<import("@/types").MealEntry> {
  const { data } = await api.get<import("@/types").MealEntry>(`/nutrition/meals/${id}`);
  return data;
}

export async function addMealItem(
  mealId: number,
  payload: import("@/types").MealItemCreate,
): Promise<import("@/types").MealWithItemResponse> {
  const { data } = await api.post<import("@/types").MealWithItemResponse>(
    `/nutrition/meals/${mealId}/items`,
    payload,
  );
  return data;
}

export async function updateMealItem(
  mealId: number,
  itemId: number,
  payload: import("@/types").MealItemUpdate,
): Promise<import("@/types").MealWithItemResponse> {
  const { data } = await api.patch<import("@/types").MealWithItemResponse>(
    `/nutrition/meals/${mealId}/items/${itemId}`,
    payload,
  );
  return data;
}

export async function deleteMealItem(
  mealId: number,
  itemId: number,
): Promise<import("@/types").MealWithItemResponse> {
  const { data } = await api.delete<import("@/types").MealWithItemResponse>(
    `/nutrition/meals/${mealId}/items/${itemId}`,
  );
  return data;
}


// --- Meal photos + metadata ---------------------------------------------

export async function addMealPhoto(
  mealId: number,
  file: File | Blob,
  opts: { caption?: string; analyze?: boolean } = {},
): Promise<import("@/types").MealPhotoWithMealResponse> {
  const fd = new FormData();
  fd.append("photo", file, "meal.jpg");
  if (opts.caption) fd.append("caption", opts.caption);
  fd.append("analyze", String(opts.analyze !== false));
  const { data } = await api.post<import("@/types").MealPhotoWithMealResponse>(
    `/nutrition/meals/${mealId}/photos`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function deleteMealPhoto(
  mealId: number,
  photoId: number,
): Promise<import("@/types").MealPhotoWithMealResponse> {
  const { data } = await api.delete<import("@/types").MealPhotoWithMealResponse>(
    `/nutrition/meals/${mealId}/photos/${photoId}`,
  );
  return data;
}

export async function updateMealMetadata(
  mealId: number,
  payload: import("@/types").MealMetadataUpdate,
): Promise<import("@/types").MealEntry> {
  const { data } = await api.patch<import("@/types").MealEntry>(
    `/nutrition/meals/${mealId}`,
    payload,
  );
  return data;
}

export async function reanalyzeMeal(
  mealId: number,
  payload: import("@/types").ReanalyzeMealIn = {},
): Promise<import("@/types").MealEntry> {
  const { data } = await api.post<import("@/types").MealEntry>(
    `/nutrition/meals/${mealId}/reanalyze`,
    payload,
  );
  return data;
}


export async function getCalorieBalance(): Promise<import("@/types").CalorieBalance> {
  const { data } = await api.get<import("@/types").CalorieBalance>(
    "/nutrition/today/balance",
  );
  return data;
}
